import {
  ASKS,
  OWNER,
  say,
  type Voice,
} from '@/james-playground/aa-family-claim/claim/asks';
import { type Row, type Value } from '@/james-playground/aa-family-claim/claim/facts';
import { ENTRY, columnsOf } from '@/james-playground/aa-family-claim/claim/registry';

/**
 * Short names and readable values for every entry, for the found card, the
 * Check step and the adviser view. The form's own wording stays in the
 * registry; these are what a person scans.
 */

const KIND = {
  difficulty: 'struggles with it',
  help: 'needs hands-on help',
  prompt: 'needs reminding',
} as const;

const NAMED: Record<string, string> = {
  surname: 'Surname',
  firstNames: 'First names',
  otherNames: 'Other names',
  title: 'Title',
  niNumber: 'National Insurance number',
  dob: 'Date of birth',
  sex: 'Sex',
  address: 'Address',
  postcode: 'Postcode',
  mobile: 'Mobile',
  phone: 'Daytime phone',
  nationality: 'Nationality',
  eeaBefore2021: 'Lived in the UK before 2021',
  normallyLiveGb: 'Normally lives in Great Britain',
  welshContact: 'Contact in Welsh',
  abroadOverFourWeeks: 'Abroad more than 4 weeks in the last 3 years',
  abroadFrom: 'Abroad from',
  abroadTo: 'Abroad until',
  abroadWhere: 'Where abroad',
  abroadReason: 'Why abroad',
  eeaBenefits: 'Pensions or benefits from the EEA or Switzerland',
  eeaWorkInsurance: 'Work or insurance in the EEA or Switzerland',
  movedFromScotland: 'Moved from Scotland',
  scotlandMoveDate: 'Moved from Scotland on',
  padpClaim: 'Claimed Pension Age Disability Payment',
  specialRules: 'Quicker route',
  accommodation: 'Type of home',
  toiletLocation: 'Toilet',
  toiletOtherLocation: 'Toilet, other',
  sleepLocation: 'Where she sleeps',
  sleepOtherLocation: 'Where she sleeps, other',
  aidsTable: 'Aids and adaptations',
  illnessesTable: 'Conditions and medicines',
  seenProfessional: 'Seen other professionals',
  professionalsTable: 'Other professionals',
  hasHelper: 'Someone helps her',
  helpersTable: 'People who help',
  gpName: 'GP surgery',
  gpAddress: 'GP address',
  gpPostcode: 'GP postcode',
  gpPhone: 'GP phone',
  gpLastSeen: 'Last saw the GP',
  hasReports: 'Reports to send',
  surgeryWaitingList: 'On a waiting list for surgery',
  surgeryWaitingListTable: 'Surgery waiting list',
  hadTests: 'Had tests',
  testsTable: 'Tests',
  careStartDate: 'Needed help since',
  dayCareDaysPerWeek: 'Days a week she needs help',
  daySupervisionDaysPerWeek: 'Days a week someone keeps an eye on her',
  nightCareNightsPerWeek: 'Nights a week she needs help',
  nightSupervisionNightsPerWeek: 'Nights a week someone watches over her',
  safeAloneDuration: 'Can be left alone for',
  nightSupervisionTimes: 'Times a night someone watches over her',
  nightSupervisionDuration: 'How long each time',
  falls: 'What happens when she falls',
  fallsClinicReferral: 'Referred to a falls clinic',
  needsHelpAfterFall: 'Needs help to get up after a fall',
  gettingUpAfterFall: 'Getting up after a fall',
  lastFall: 'Last fall',
  lastStumble: 'Last stumble',
  fallsLastMonth: 'Falls in the last month',
  fallsLastYear: 'Falls in the last year',
  stumblesLastMonth: 'Stumbles in the last month',
  stumblesLastYear: 'Stumbles in the last year',
  homeActivities: 'Activities at home',
  outsideActivities: 'Activities out and about',
  currentStay: 'In a hospital, hospice or care home now',
  currentStayPlaceType: 'Where she is staying',
  currentStayFrom: 'Staying there since',
  currentStayName: 'Place name',
  currentStayAddress: 'Place address',
  currentStayTown: 'Town',
  currentStayPostcode: 'Place postcode',
  currentStayReason: 'Why she is there',
  publiclyFundedStay: 'A public body pays part of it',
  fundingAuthority: 'Who pays',
  previousStayLastSixWeeks: 'A stay in the last 6 weeks',
  previousStayPlaceType: 'Where she stayed',
  previousStayFrom: 'Stayed from',
  previousStayTo: 'Stayed until',
  previousStayName: 'Place name',
  previousStayAddress: 'Place address',
  previousStayTown: 'Town',
  previousStayPostcode: 'Place postcode',
  previousStayReason: 'Why she was there',
  constantAttendance: 'Constant Attendance Allowance',
  bankAccountHolder: 'Account holder',
  bankName: 'Bank or building society',
  bankSortCode: 'Sort code',
  bankAccountNumber: 'Account number',
  bankAccountHasRollNumber: 'Has a roll number',
  bankRollNumber: 'Roll number',
  otherBenefitsToAccount: 'Other benefits to this account',
  signingForSomeoneElse: 'Someone else signs for her',
  signerCategory: 'Why they sign',
  poaOnlineAccount: 'Attorney has an online account',
  representativeOrganisation: 'Organisation',
  representativeName: 'Signer’s name',
  representativeNiNumber: 'Signer’s NI number',
  representativeDob: 'Signer’s date of birth',
  representativeAddress: 'Signer’s address',
  representativePostcode: 'Signer’s postcode',
  representativePhone: 'Signer’s phone',
  dwpMedicalContactConsent: 'DWP can contact her doctors',
  declarationPrintedName: 'Name under the declaration',
  attachments: 'Documents sent with the form',
  variability: 'How it varies',
  extraInformation: 'Anything else',
  supportingStatementWanted: 'Supporting statement',
};

/** The short name for one entry. */
export const labelOf = (id: string, v: Voice): string => {
  const owner = OWNER[id];
  if (owner?.gate === id) return say(owner.title, v);
  for (const a of ASKS) {
    for (const row of a.rows ?? []) {
      const kind = (Object.keys(KIND) as (keyof typeof KIND)[]).find((k) => row[k] === id);
      if (kind) return `${say(row.label, v)}: ${KIND[kind]}`;
    }
    for (const row of a.nightRows ?? []) {
      if (row.times === id) return `${say(row.label, v)} at night: times`;
      if (row.minutes === id) return `${say(row.label, v)} at night: minutes`;
    }
  }
  if (NAMED[id]) return say(NAMED[id], v);
  const e = ENTRY[id];
  if (e?.label) return say(e.label, v);
  if (/TellUsMore$/.test(id)) return 'Something to add';
  if (/Reason/.test(id) && e?.prompt) return e.prompt;
  const first = (e?.prompt ?? id).split(/[?.:]/)[0];
  return first.length > 64 ? `${first.slice(0, 61)}…` : first;
};

const UNIT: Record<string, (n: string) => string> = {
  count_per_day: (n) => `${n} ${n === '1' ? 'time' : 'times'} a day`,
  count_per_night: (n) => `${n} ${n === '1' ? 'time' : 'times'} a night`,
  minutes: (n) => `${n} minutes`,
  days_per_week: (n) => `${n} ${n === '1' ? 'day' : 'days'} a week`,
  nights_per_week: (n) => `${n} ${n === '1' ? 'night' : 'nights'} a week`,
};

const rowText = (tableId: string, row: Row) => {
  const columns = columnsOf(tableId);
  const ids = columns.length ? columns.map((c) => c.id) : Object.keys(row);
  return ids
    .map((id) => row[id])
    .filter(Boolean)
    .join(', ');
};

/** A value as a person reads it: "2 times a day", "Walking frame". */
export const valueText = (id: string, value: Value | undefined): string => {
  if (value === undefined) return '';
  const e = ENTRY[id];
  if (Array.isArray(value)) {
    if (value.length && typeof value[0] === 'object')
      return (value as Row[]).map((row) => rowText(id, row)).filter(Boolean).join('; ');
    return (value as string[]).join(', ');
  }
  if (!value) return 'None';
  const unit = e && UNIT[e.answerType];
  if (unit && /^\d+$/.test(value)) return unit(value);
  return value.replace(/\n/g, ', ');
};
